
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import AddDocument from '../../document/AddDocument.vue';
import { Document } from '../../../../../../common/application/model/Document';
import { getDatabaseOptions, OptionValue } from '../../../../../framework/service/options';
import ViewDocuments from '../../document/ViewDocuments.vue';
import { getResources, postResource } from '../../../../../framework/client/resource';
import { FileMeta } from '../../../../../../common/application/model/FileMeta';
import { errorToast } from '../../../../service/toast_service';
import { sharedState } from '../../../../../framework/state';
import { ValidationObserver } from 'vee-validate';
import TextField from '../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import { parseTraficomCsv } from '../../../../client/vehicle_contract_client';
import { SortOrder } from '../../../../../../common/framework/model/SortOrder';
import ImportVehicleContractsTable from './ImportVehicleContractsTable.vue';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';

@Component({
    components: {
        SelectField,
        TextField,
        ViewDocuments,
        AddDocument,
        ImportVehicleContractsTable,
        ValidationObserver,
    },
})
export default class ImportVehicleContracts extends Vue {
    @Prop(String) readonly customerId!: string;

    readonly resourceType = ApplicationResource.CUSTOMER;

    shared = sharedState;
    local = {
        loading: false,
        droppedDocument: null as Document | null,
        uploadedCsv: { public: false } as FileMeta,
        uploading: false,
        parsing: false,
        fileUploaded: false,
        categoryOptions: [] as OptionValue[],
        existingCsvFiles: [] as FileMeta[],
        rows: [] as VehicleContract[],
    };

    async mounted() {
        this.local.loading = true;
        await this.loadAsyncData();
        this.local.loading = false;
    }

    @Watch('local.droppedDocument')
    selectedDocumentChanged() {
        if (!this.local.droppedDocument) {
            return;
        }

        const traficomCsvCategory = this.local.categoryOptions.find((option) => option.label === 'Traficom CSV');

        if (traficomCsvCategory && traficomCsvCategory.id) {
            this.local.droppedDocument.category = traficomCsvCategory.id;
        }
        this.local.droppedDocument.uploadName = this.local.droppedDocument.name.normalize('NFC');
    }

    async add() {
        try {
            if (!this.local.droppedDocument) {
                return;
            }

            const documentType = this.local.droppedDocument.name.substring(
                this.local.droppedDocument.name.lastIndexOf('.') + 1,
            );
            if (!this.local.droppedDocument.uploadName.includes('.' + documentType)) {
                this.local.droppedDocument.uploadName = this.local.droppedDocument.uploadName + '.' + documentType;
            }

            this.local.uploadedCsv = await postResource<FileMeta>(ApplicationResource.FILE_META, {
                ...this.local.uploadedCsv,
                created: new Date(),
                modified: new Date(),
                name: this.local.droppedDocument.uploadName,
                mimeType: this.local.droppedDocument.type,
                fileExtension: documentType,
                size: this.local.droppedDocument.size,
                category: this.local.droppedDocument.category,
                description: this.local.droppedDocument.description || '',
                resourceId: this.customerId,
                resourceType: this.resourceType as ApplicationResource,
                customerId: this.customerId ? this.customerId : null,
            });

            this.upload(this.local.uploadedCsv, 0);
        } catch (error) {
            console.warn(error);
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    private async loadAsyncData() {
        this.local.uploadedCsv = {
            ...this.local.uploadedCsv,
            userId: this.shared.context.userId,
        };

        this.local.categoryOptions = (await getDatabaseOptions('DocumentCategory', this)).filter(
            ({ id }) => id !== undefined,
        );

        const parameters = new Map<string, any>([
            ['customerId', this.customerId],
            ['sortField', 'created'],
            ['sortOrder', SortOrder.DESC],
            ['category', 'traficom-csv'],
        ]);
        this.local.existingCsvFiles = await getResources<FileMeta>(ApplicationResource.FILE_META, 0, parameters, 10);
    }

    private upload(uploadedDocument: FileMeta, index: number) {
        this.local.uploading = true;

        try {
            if (!this.local.droppedDocument) {
                return;
            }

            const formData = new FormData();
            let file: any = this.local.droppedDocument;
            let name = file.uploadName;
            file = new File([file], name, { type: file.type });

            formData.append('fileBinary', file);
            formData.append('fileId', uploadedDocument.id);

            const xhr = new XMLHttpRequest();

            xhr.addEventListener('loadend', () => {
                this.local.uploading = false;
            });

            xhr.addEventListener('error', () => {
                errorToast(this);
                this.local.uploading = false;
            });

            xhr.addEventListener('abort', () => {
                this.local.uploading = false;
            });

            xhr.onreadystatechange = async () => {
                if (xhr.readyState === 4) {
                    if (xhr.status !== 200) {
                        console.warn('Upload returned error status: ' + xhr.status);
                        errorToast(this);
                    }

                    if (xhr.status === 200 || xhr.status == 301) {
                        this.local.fileUploaded = true;
                        await this.importTraficomCsv();
                    }
                }
            };

            xhr.open(
                'POST',
                `/api/file/resource/${this.resourceType}/category/${uploadedDocument.category}?path=excel-import`,
                true,
            );
            xhr.setRequestHeader('Authorization', `Bearer ${sharedState.context.idToken}`);
            xhr.send(formData);
        } catch (error) {
            console.warn(error);
            errorToast(this);
        }
    }

    private async importTraficomCsv(previousFile?: FileMeta): Promise<void> {
        this.local.parsing = true;
        this.local.rows = [];

        if (previousFile) {
            this.local.fileUploaded = true;
            this.local.rows = await parseTraficomCsv(`${previousFile.id}_${previousFile.name}`, this.customerId);
        } else {
            this.local.rows = await parseTraficomCsv(
                `${this.local.uploadedCsv.id}_${this.local.uploadedCsv.name}`,
                this.customerId,
            );
        }
        this.local.parsing = false;
    }
}
